import { Npc, select } from "gamedata/GameData";
import { ReactNode, useState } from "react";
import { Link, useLoaderData } from "react-router";
import { z } from "zod";
import { CheckBox } from "~/components/form/CheckBox";
import { ControlBox } from "~/components/form/ControlBox";
import { Input } from "~/components/form/Input";
import { MultiSelect } from "~/components/form/MultiSelect";
import { LevelSA } from "~/components/npc/LevelSA";
import { NpcHpRate } from "~/components/npc/NpcHpRate";
import { NpcLink } from "~/components/npc/NpcLink";
import { NpcStrengths } from "~/components/npc/NpcStrengths";
import { NpcWeakness } from "~/components/npc/NpcWeakness";
import { EntityTooltip } from "~/components/site/Tooltip";
import { Breadcrumb } from "~/components/ui/Breadcrumb";
import { Button } from "~/components/ui/Button";
import { Icon16 } from "~/components/ui/Icon";
import { PageTitle } from "~/components/ui/PageTitle";
import { Table } from "~/components/ui/Table";
import {
  NPC_TYPE_NAMES,
  NpcType,
  RACE_NAMES,
  RaceUname,
  WEAKNESS_NAMES,
  WeaknessId,
  xRateId,
} from "~/types";
import { createFiltering } from "~/util/Filtering";
import { GameVer, lstring, tt, useRealm, useT, useTurl } from "~/util/Lang";
import { Pager, applyPaging } from "~/util/Listing";
import type { MetaFunc, OurLoaderArgs } from "~/util/Loaders";
import { SortColumnName, createSorting, useUrlSorting } from "~/util/Sorting";
import {
  urlQueryFromSearchParams,
  useSetUrlQuery,
  useUrlQuery,
} from "~/util/UrlUtils";
import { gameName } from "~/util/common";
import { getLocList } from "~/util/loc";
import { getGenSkillsGameVer } from "~/util/skill";
import { logPageView } from "~/util/stats";
import { homePage } from "~/util/titles";

const PageQuery = z.object({
  p: z.optional(z.string()),
  s: z.optional(z.string()),
});

type ItemFilter = {
  minLevel: null | number;
  maxLevel: null | number;
  race: readonly string[];
  weakness: readonly string[];
  strong: readonly string[];
  type: readonly string[];
  herb: null | boolean;
  sa: null | boolean;
};

const SORTER = createSorting<Npc>({
  lvl: (a, b) => (a.level ?? 0) - (b.level ?? 0),
  exp: (a, b) => (a.exp ?? 0) - (b.exp ?? 0),
  race: (a, b) =>
    (a.race?.raceName ?? "").localeCompare(b.race?.raceName ?? ""),
});

const FILTER = (gver: GameVer) =>
  createFiltering<ItemFilter, Npc>({
    pack: (f) => ({
      min: f.minLevel != null ? f.minLevel.toString() : undefined,
      max: f.maxLevel != null ? f.maxLevel.toString() : undefined,
      race: f.race.join(","),
      weakness: f.weakness.join(","),
      strong: f.strong.join(","),
      type: f.type.join(","),
      herb: f.herb != null && f.herb != false ? f.herb.toString() : undefined,
      sa: f.sa != null && f.sa != false ? f.sa.toString() : undefined,
    }),
    unpack: (r) => {
      return {
        minLevel: r.min ? parseInt(r.min) : null,
        maxLevel: r.max ? parseInt(r.max) : null,
        race: r.race ? r.race.split(",") : [],
        weakness: r.weakness ? r.weakness.split(",") : [],
        strong: r.strong ? r.strong.split(",") : [],
        type: NpcType.array().parse(r.type?.split(",") ?? []),
        herb: r.herb ? true : null,
        sa: r.sa ? true : null,
      };
    },
    filter: (item, f) => {
      if (typeof f.minLevel === "number" && (item.level ?? 0) < f.minLevel) {
        return false;
      }
      if (
        typeof f.maxLevel === "number" &&
        (item.level ?? Number.MAX_SAFE_INTEGER) > f.maxLevel
      ) {
        return false;
      }
      if (f.race.length > 0 && !f.race.includes(item.race?.raceName)) {
        return false;
      }
      if (
        f.weakness.length > 0 &&
        item.skills.filter((x) => f.weakness.includes(x.id.toString()))
          .length === 0
      ) {
        return false;
      }
      if (
        f.strong.length > 0 &&
        item.skills.filter((s) =>
          f.strong
            .map((xr) => xRates[getGenSkillsGameVer(gver)][xr as xRateId].skill)
            .includes(s.id + "_" + s.level)
        ).length === 0
      ) {
        return false;
      }
      if (f.herb && !item.herbList.length) {
        return false;
      }
      if (f.type.length > 0 && !f.type.includes(item.type)) {
        return false;
      }
      if (f.sa && !item.canUseSA) {
        return false;
      }
      return true;
    },
  });

async function makeData(la: OurLoaderArgs) {
  // Log
  logPageView(la);
  //
  const t = tt.bind(null, la.realm.lang);
  const query = PageQuery.parse(
    Object.fromEntries(la.url.searchParams.entries())
  );
  const filter = FILTER(la.realm.game).fromUrlQuery(
    urlQueryFromSearchParams(la.url.searchParams)
  );

  let all = Array.from(la.gd.npcs.values());
  const sorted = SORTER.apply(all, query.s);
  const filtered = FILTER(la.realm.game).apply(sorted, filter);
  const [paged, paging] = applyPaging(filtered, query);

  const npcs = select(paged, {
    select: {
      id: true,
      npcName: true,
      name: t,
      nick: t,
      nickColor: true,
      type: true,
      level: true,
      agro: true,
      race: { raceName: true, icon: true, name: { en: true, ru: true } },
      skills: {
        select: { id: true, skillName: true, level: true, name: t, icon: true },
      },
      locs: {
        select: { locName: true, name: t },
      },
      herbList: { select: { item: { id: true } } },
      exp: true,
      canUseSA: true,
    },
  });

  return { npcs, paging };
}

type LoaderData = Awaited<ReturnType<typeof makeData>>;

export const loader = async (la: OurLoaderArgs) => {
  const data = await makeData(la);
  return { ...data, authUser: la.req.sess?.user };
};

export const meta: MetaFunc<LoaderData> = (cx) => {
  const { data } = cx;
  let title = cx.t({ en: "NPCs", ru: "НПЦ" });
  if (data.paging.page > 1) {
    title += ` (${cx.t({ en: "Page", ru: "Страница" })} ${data.paging.page})`;
  }

  return {
    title: `${title} - ${homePage(cx.realm)}`,
    description: cx.t({
      en: `A complete searchable and filterable list of all Items in ${gameName(
        cx.realm.game
      )}`,
      ru: `Полный список всех НПЦ ${gameName(
        cx.realm.game
      )} с возможностью фильтрации, Боссы, Квестовые монстры, Торговцы, Гварды, Учителя, Кузнецы.`,
    }),
    ogTitle: cx.t({
      en: `NPCs ${gameName(cx.realm.game)}`,
      ru: `НПЦ ${gameName(cx.realm.game)}`,
    }),
  };
};

export default function Npcs() {
  const data = useLoaderData() as LoaderData;
  const t = useT();
  const { game } = useRealm();
  const turl = useTurl();
  const pager = <Pager paging={data.paging} />;
  const npcsTitle = t({ en: "NPCs", ru: "Мобы" });
  const breadcrumbList = [{ title: npcsTitle, url: turl("/npcs") }];

  const urlSorting = useUrlSorting();

  return (
    <div>
      <div className="content">
        <Breadcrumb items={breadcrumbList} />
        <PageTitle>{npcsTitle}</PageTitle>
        <Filters />
      </div>
      {pager}
      <div style={{ overflowX: "auto" }}>
        <Table
          data={data.npcs}
          id={(npc) => npc.id}
          cols={[
            {
              title: t({ en: "Name", ru: "Имя" }),
              cell: (npc) => (
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <NpcLink npc={npc} showLvl={false} />
                  <div style={{ fontSize: 12, maxWidth: 400 }}>
                    {getLocList(npc.locs)}
                  </div>
                </div>
              ),
            },
            {
              title: (
                <SortColumnName name="lvl" by={urlSorting}>
                  {t({ en: "Level", ru: "Уровень" })}
                </SortColumnName>
              ),
              cell: (npc) => npc.level ?? "",
            },
            {
              title: "Rate",
              cell: (npc) => <NpcHpRate skills={npc.skills} game={game} />,
            },
            {
              title: t({ en: "Strengths", ru: "Усиления" }),
              cell: (npc) => <NpcStrengths skills={npc.skills} />,
            },
            {
              title: t({ en: "Weaknesses", ru: "Слабости" }),
              cell: (npc) => <NpcWeakness skills={npc.skills} />,
            },
            {
              title: t({ en: "SA", ru: "SA" }),
              cell: (npc) => <LevelSA value={npc.canUseSA} />,
              hide: game === "c1",
            },
            {
              title: t({ en: "Herbs", ru: "Хербы" }),
              cell: (npc) =>
                npc.herbList.length > 0 && (
                  <Icon16 icon="etc_herb_blue_i00" alt="Drop herbs" />
                ),
              hide: getGenSkillsGameVer(game) === "OLD",
            },
            {
              title: (
                <SortColumnName name="exp" by={urlSorting}>
                  {t({ en: "Exp", ru: "Опыт" })}
                </SortColumnName>
              ),
              cell: (npc) => (npc.exp ? Math.round(npc.exp) : ""),
            },
            {
              title: t({ en: "Aggr", ru: "Агро" }),
              cell: (npc) =>
                (() => {
                  if (npc.agro == null) {
                    return null;
                  }
                  return (
                    <span
                      style={{
                        color: npc.agro ? "red" : "green",
                        fontSize: "12px",
                        fontWeight: 600,
                      }}
                    >
                      {npc.agro
                        ? t({ en: "YES", ru: "ДА" })
                        : t({ en: "NO", ru: "НЕТ" })}
                    </span>
                  );
                })(),
            },
            {
              title: (
                <SortColumnName name="race" by={urlSorting}>
                  {t({ en: "Race", ru: "Раса" })}
                </SortColumnName>
              ),
              cell: (npc) => npc.race && npc.race.name.en,
            },
          ]}
        />
      </div>
      {pager}
    </div>
  );
}

function Filters(props: { pages?: ReactNode }) {
  const t = useT();
  const { game } = useRealm();
  const urlQuery = useUrlQuery();
  const setUrlQuery = useSetUrlQuery();

  const [filter, setFilter] = useState(() =>
    FILTER(game).fromUrlQuery(urlQuery)
  );

  return (
    <div style={{ padding: "0 5px" }}>
      <div style={{ display: "flex", gap: 20 }}>
        <div>
          <ControlBox
            title="Min level"
            reset={() => setFilter((f) => ({ ...f, minLevel: null }))}
          >
            <Input
              value={filter.minLevel == null ? "" : filter.minLevel.toString()}
              onChange={(e) =>
                setFilter((f) => ({
                  ...f,
                  minLevel: parseInt(e.target.value) || null,
                }))
              }
            />
          </ControlBox>
          <div style={{ height: 10 }} />
          <ControlBox
            title="Max level"
            reset={() => setFilter((f) => ({ ...f, maxLevel: null }))}
          >
            <Input
              value={filter.maxLevel == null ? "" : filter.maxLevel.toString()}
              onChange={(e) =>
                setFilter((f) => ({
                  ...f,
                  maxLevel: parseInt(e.target.value) || null,
                }))
              }
            />
          </ControlBox>
        </div>
        <ControlBox
          title="NPC Type"
          reset={() => setFilter((f) => ({ ...f, type: [] }))}
        >
          <MultiSelect
            options={NpcType.options.map((nt) => ({
              value: nt,
              title: t(NPC_TYPE_NAMES[nt]),
            }))}
            value={filter.type}
            onChange={(nt) => setFilter((f) => ({ ...f, type: nt }))}
          />
        </ControlBox>
        <ControlBox
          title="Race"
          reset={() => setFilter((f) => ({ ...f, race: [] }))}
        >
          <MultiSelect
            options={RaceUname.options.map((wt) => ({
              value: wt,
              title: t(RACE_NAMES[wt]),
            }))}
            value={filter.race}
            onChange={(wt) => setFilter((f) => ({ ...f, race: wt }))}
          />
        </ControlBox>
        <ControlBox
          title="Weakness"
          reset={() => setFilter((f) => ({ ...f, weakness: [] }))}
        >
          <MultiSelect
            options={WeaknessId.options.map((wt) => ({
              value: wt,
              title: t(WEAKNESS_NAMES[wt]),
            }))}
            value={filter.weakness}
            onChange={(wt) => setFilter((f) => ({ ...f, weakness: wt }))}
          />
        </ControlBox>
        <ControlBox
          title="X Rate"
          reset={() => setFilter((f) => ({ ...f, strong: [] }))}
        >
          <MultiSelect
            options={xRateId.options.map((xr) => ({
              value: xr,
              title: t(xRates[getGenSkillsGameVer(game)][xr].name),
            }))}
            value={filter.strong}
            onChange={(wt) => setFilter((f) => ({ ...f, strong: wt }))}
          />
        </ControlBox>

        <ControlBox
          title="Other"
          reset={() => setFilter((f) => ({ ...f, herb: false, sa: false }))}
        >
          {getGenSkillsGameVer(game) === "NEW" && (
            <CheckBox
              id="herbs"
              checked={filter.herb ? true : false}
              onChange={(e) =>
                setFilter((f) => ({
                  ...f,
                  herb: !f.herb,
                }))
              }
              label="Drop Herbs"
            />
          )}
          {game !== "c1" && (
            <CheckBox
              id="sa"
              checked={filter.sa ? true : false}
              onChange={(e) =>
                setFilter((f) => ({
                  ...f,
                  sa: !f.sa,
                }))
              }
              label="Can Use SA"
            />
          )}
        </ControlBox>
      </div>

      <div
        style={{
          alignItems: "flex-end",
          display: "flex",
          justifyContent: "space-between",
          paddingTop: 20,
        }}
      >
        <div style={{ padding: "0 0 8px" }}>
          <Button
            onClick={() => {
              setUrlQuery(
                { ...urlQuery, f: FILTER(game).encode(filter) },
                { preventScrollReset: true }
              );
            }}
          >
            Apply
          </Button>
        </div>
        {props.pages}
      </div>
    </div>
  );
}

const xRates: Record<
  "OLD" | "NEW",
  Record<xRateId, { skill: string; name: lstring }>
> = {
  OLD: {
    x1_2: { skill: "4311_1", name: { en: "x1/2", ru: "x1/2" } },
    x2: { skill: "4303_1", name: { en: "x2", ru: "x2" } },
    x3: { skill: "4304_1", name: { en: "x3", ru: "x3" } },
    x4: { skill: "4305_1", name: { en: "x4", ru: "x4" } },
    x5: { skill: "4306_1", name: { en: "x5", ru: "x5" } },
  },
  NEW: {
    x1_2: { skill: "4408_9", name: { en: "x1/2", ru: "x1/2" } },
    x2: { skill: "4408_10", name: { en: "x2", ru: "x2" } },
    x3: { skill: "4408_11", name: { en: "x3", ru: "x3" } },
    x4: { skill: "4408_12", name: { en: "x4", ru: "x4" } },
    x5: { skill: "4408_13", name: { en: "x5", ru: "x5" } },
  },
};
